import { toast } from "react-toastify";
import {
  OBJECT_TYPES,
  ELECTRICAL_ELEMENT_TYPES,
  SANITARY_ELEMENT_TYPES,
  ELEMENT_GROUP_LIMITS,
  GRID_CELL_SIZE_M,
} from "../../configuratorConstants";
import { distance } from "../../utils/geometry";

export const handleElementAction = (state, action, context) => {
  const { currentFloorObjects, t } = context;

  switch (action.type) {
    case "TOGGLE_ELEMENT_PLACEMENT_SIDE": {
      if (!state.selectedObjectId) return { handled: true, directState: state };
      const nextStateObjects = JSON.parse(JSON.stringify(currentFloorObjects));
      let wasMutated = false;
      let restrictionFailed = false;

      const performSmartToggle = (element, containerElements, segmentLengthM) => {
        const newPlacementSide = (element.placementSide || 1) * -1;
        const isElectrical = ELECTRICAL_ELEMENT_TYPES.includes(element.type);
        const limit = isElectrical ? ELEMENT_GROUP_LIMITS.ELECTRICAL : 1;

        if (!isElectrical) {
          if (SANITARY_ELEMENT_TYPES.includes(element.type)) {
            const occupied = containerElements.some(
              (e) =>
                e.id !== element.id &&
                SANITARY_ELEMENT_TYPES.includes(e.type) &&
                (e.placementSide || 1) === newPlacementSide,
            );
            if (occupied) {
              setTimeout(
                () => toast.warn(t("toasts.sanitaryElementOnePerSandwichError")),
                0,
              );
              restrictionFailed = true;
              return false;
            }
          }
          element.placementSide = newPlacementSide;
          return true;
        }

        const numSlots = Math.max(1, Math.round(segmentLengthM / GRID_CELL_SIZE_M));
        const currentPos = element.positionOnSegment ?? 0.5;
        const targetSlotIndex = Math.max(
          0,
          Math.min(numSlots - 1, Math.floor(currentPos * numSlots)),
        );

        const elementsInTargetSlot = containerElements.filter(
          (e) =>
            e.id !== element.id &&
            ELECTRICAL_ELEMENT_TYPES.includes(e.type) &&
            (e.placementSide || 1) === newPlacementSide &&
            Math.max(
              0,
              Math.min(numSlots - 1, Math.floor((e.positionOnSegment ?? 0.5) * numSlots)),
            ) === targetSlotIndex,
        );

        const allCandidates = [element, ...elementsInTargetSlot];

        if (allCandidates.length > limit) {
          const errorMessage = `Максимум ${limit} електричних елементів на сегмент з кожної сторони`;
          setTimeout(() => toast.warn(errorMessage), 0);
          restrictionFailed = true;
          return false;
        }

        element.placementSide = newPlacementSide;

        if (allCandidates.length > 0) {
          const slotStart = targetSlotIndex / numSlots;
          const slotWidth = 1 / numSlots;

          allCandidates.sort(
            (a, b) => (a.positionOnSegment ?? 0.5) - (b.positionOnSegment ?? 0.5),
          );

          allCandidates.forEach((el, idx) => {
            const relativePos = (idx + 1) / (allCandidates.length + 1);
            el.positionOnSegment = slotStart + relativePos * slotWidth;
          });
        }

        return true;
      };

      const findAndMutate = (objects) => {
        for (const obj of objects) {
          if (obj.elements) {
            const el = obj.elements.find((e) => e.id === state.selectedObjectId);
            if (el) {
              if (obj.type === OBJECT_TYPES.DOCKING_JOINT) {
                return performSmartToggle(el, obj.elements, obj.width);
              }
            }
          }

          if (obj.wallSegments) {
            for (const wall of Object.values(obj.wallSegments)) {
              if (wall.elements) {
                const el = wall.elements.find((e) => e.id === state.selectedObjectId);
                if (el) {
                  return performSmartToggle(el, wall.elements, GRID_CELL_SIZE_M);
                }
              }
            }
          }

          if (obj.type === OBJECT_TYPES.FRAMELESS_BUILDING) {
            const allWalls = [
              ...(obj.perimeterWalls || []),
              ...(obj.internalWalls || []),
            ];
            for (const wall of allWalls) {
              for (const seg of wall.segments) {
                if (seg.elements) {
                  const el = seg.elements.find((e) => e.id === state.selectedObjectId);
                  if (el) {
                    return performSmartToggle(el, seg.elements, seg.length);
                  }
                }
              }
            }
          }

          if (
            obj.type === OBJECT_TYPES.FRAMELESS_PARTITION ||
            obj.type === OBJECT_TYPES.FRAMELESS_WALL
          ) {
            if (obj.segments) {
              for (const seg of obj.segments) {
                if (seg.elements) {
                  const el = seg.elements.find((e) => e.id === state.selectedObjectId);
                  if (el) {
                    return performSmartToggle(el, seg.elements, seg.length);
                  }
                }
              }
            }
            if (obj.elements) {
              const el = obj.elements.find((e) => e.id === state.selectedObjectId);
              if (el) {
                const len =
                  obj.startPoint && obj.endPoint
                    ? distance(obj.startPoint, obj.endPoint)
                    : GRID_CELL_SIZE_M;
                return performSmartToggle(el, obj.elements, len);
              }
            }
          }

          if (wasMutated) break;
        }
        return false;
      };

      wasMutated = findAndMutate(nextStateObjects);
      if (!wasMutated || restrictionFailed) {
        return { handled: true, directState: state };
      }

      return {
        handled: true,
        nextStateObjects,
        needsValidation: true,
      };
    }

    case "SET_ELEMENT_PROPERTY": {
      const { elementId, property, value } = action.payload;
      const nextStateObjects = JSON.parse(JSON.stringify(currentFloorObjects));

      let elementFound = false;

      const findAndUpdate = (obj) => {
        if (obj.id === elementId) {
          obj[property] = value;
          elementFound = true;
          return true;
        }
        const collections = [
          'elements',
          'freeElements',
          'perimeterWalls',
          'internalWalls',
          'internalPartitions',
          'wallSegments',
          'segments',
        ];
        for (const coll of collections) {
          if (Array.isArray(obj[coll])) {
            for (const item of obj[coll]) {
              if (findAndUpdate(item)) return true;
            }
          } else if (typeof obj[coll] === 'object' && obj[coll] !== null) {
            if (obj[coll].elements) {
              if (findAndUpdate(obj[coll])) return true;
            } else {
              for (const key in obj[coll]) {
                if (findAndUpdate(obj[coll][key])) return true;
              }
            }
          }
        }
        if (obj.wallSegments) {
          for (const key in obj.wallSegments) {
            if (findAndUpdate(obj.wallSegments[key])) return true;
          }
        }
        if (obj.segments) {
          for (const seg of obj.segments) {
            if (findAndUpdate(seg)) return true;
          }
        }
        return false;
      };

      for (const obj of nextStateObjects) {
        if (findAndUpdate(obj)) break;
      }

      return {
        handled: true,
        nextStateObjects,
      };
    }

    default:
      return null;
  }
};
